import { ChangeDetectionStrategy, Component, Injector, Input, OnInit } from '@angular/core';
import { take } from 'rxjs/operators';
import { WidgetBaseComponent } from '../../widgetBase';

/**
 * GrossRentToYear1Noi Widget
 */
@Component({
  selector: 'gross-rent-to-year-1-noi-widget',
  templateUrl: './gross-rent-to-year-1-noi-widget.component.html',
  styleUrls: ['../../common/css/general.scss', './gross-rent-to-year-1-noi-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GrossRentToYear1NoiWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Are charts ready to be displayed?
   */
  chartsReady = false;

  /**
   * Chart data
   */
  public chartData: any[] = [];

  /**
   * Title
   */
  public title = {
    text: 'Will be translated', font: 'bold 16px Arial'
  };

  /**
   * Chart categories
   */
  public categories = [];

  /**
   * Chart colors
   */
  public colors = {
    increase: '#D4D5D6',
    decrease: '#9B75A8',
    total: '#626468'
  }

  /**
   * Constructor
   * @param injector
   */
  constructor(injector: Injector) {
    super(injector, 'grossRentToYear1Noi', true);
  }

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready
    this.initWidgetConfig();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler("grossRentToYear1Noi", this.provideSaveObject.bind(this));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    this.mockupData$ = this.widgetService.loadMockup('grossRentToYear1Noi').pipe(take(1));

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('grossRentToYear1Noi', this.getReport()).pipe();
  }

  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  protected preprocessLoadedData(data) {
    data.rows = [];

    this.reportDesignerTranslator.ready('grossRentToYear1Noi').then(() => {

      this.title.text = this.reportDesignerTranslator.getString('grossRentToYear1Noi.labelTitle');

      const fields = [
        { name: 'grossErv', category: this.reportDesignerTranslator.getString('grossRentToYear1Noi.grossErv') },

        { name: 'opex01NonRecoverableAmount', category: this.widgetData['opex01Label'] },
        { name: 'opex02NonRecoverableAmount', category: this.widgetData['opex02Label'] },
        { name: 'opex03NonRecoverableAmount', category: this.widgetData['opex03Label'] },
        { name: 'opex04NonRecoverableAmount', category: this.widgetData['opex04Label'] },
        { name: 'opex05NonRecoverableAmount', category: this.widgetData['opex05Label'] },
        { name: 'opex06NonRecoverableAmount', category: this.widgetData['opex06Label'] },
        { name: 'opex07NonRecoverableAmount', category: this.widgetData['opex07Label'] },
        { name: 'opex08NonRecoverableAmount', category: this.widgetData['opex08Label'] },
        { name: 'opex09NonRecoverableAmount', category: this.widgetData['opex09Label'] },
        { name: 'opex10NonRecoverableAmount', category: this.widgetData['opex10Label'] },
        { name: 'opex11NonRecoverableAmount', category: this.widgetData['opex11Label'] },
        { name: 'opex12NonRecoverableAmount', category: this.widgetData['opex12Label'] },
        { name: 'opex13NonRecoverableAmount', category: this.widgetData['opex13Label'] },
        { name: 'opex14NonRecoverableAmount', category: this.widgetData['opex14Label'] },
        { name: 'opex15NonRecoverableAmount', category: this.widgetData['opex15Label'] },
        { name: 'opex16NonRecoverableAmount', category: this.widgetData['opex16Label'] },
        { name: 'opex17NonRecoverableAmount', category: this.widgetData['opex17Label'] },
        { name: 'opex18NonRecoverableAmount', category: this.widgetData['opex18Label'] },
        { name: 'opex19NonRecoverableAmount', category: this.widgetData['opex19Label'] },
        { name: 'opex20NonRecoverableAmount', category: this.widgetData['opex20Label'] },

        { name: 'netERVTotal', category: this.reportDesignerTranslator.getString('grossRentToYear1Noi.netERVTotal'), totals: true, addOtherBefore: true },
      ];
      this.processFields(fields);

      this.chartsReady = true;
      this.ref.markForCheck();
    });

    return data;
  }

  /**
   * Process the fields
   * @param fields
   */
  private processFields(fields: any[]): void {
    this.chartData = [];
    let runningTotal = 0;

    for (let i = 0; i < fields.length; ++i) {
      const field = fields[i];
      const currentValue = this.widgetData[field.name];
      if (i === 0) {
        this.chartData.push({ category: field.category, amount: currentValue, color: this.colors.total });
        this.categories.push(field.category);
      } else if (field.totals) {
        //Automatically insert an 'Other' entry that fixes the (running) total
        this.addOtherEntries(field, runningTotal, currentValue);
      } else {
        let color;
        if (currentValue > 0) {
          color = this.colors.increase;
        }
        else {
          color = this.colors.decrease;
        }
        if (field.category || currentValue) {
          this.chartData.push({ category: field.category, amount: currentValue, color: color });
          this.categories.push(field.category);
        }
      }
      runningTotal += currentValue;
    }
  }

  /**
   * Add other entries
   * @param field
   * @param runningTotal
   * @param currentValue
   */
  private addOtherEntries(field: any, runningTotal: number, currentValue: any): void {
    if (field.addOtherBefore && runningTotal != currentValue) {
      const difference = currentValue - runningTotal;
      let color;
      if (difference > 0) {
        color = this.colors.increase;
      }
      else {
        color = this.colors.decrease;
      }
      this.chartData.push({ category: this.reportDesignerTranslator.getString('grossRentToYear1Noi.labelOther'), amount: difference, color: color });
      this.categories.push(this.reportDesignerTranslator.getString('grossRentToYear1Noi.labelOther'));
    }

    this.chartData.push({ category: field.category, amount: currentValue, color: this.colors.total, summary: 'total' });
    this.categories.push(field.category);
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    return this.widgetData;
  }

  /**
   * Legend item clicked event handler
   * @param e {any} Event object
   */
  public onLegendItemClick(e: any): void {
    /* Do not hide the series on legend click */
    e.preventDefault();
  }
}
